import React, { useEffect, useState } from "react";
import { Avatar, Divider, Form, Select, DatePicker } from "antd";
import clsx from "clsx";
import { useIntl } from "react-intl";
import styled from "styled-components";
import {
  getInteraction,
  getOperationDashboard,
  toggleSpinner
} from "../../redux/actions";
import * as constants from "../../constants";
import { getSecuredUrl } from "../../utils/utilities";
import StyledText from "../../components/StyledText";
import MediaCarousel from "../../components/MediaCarousel";
import {
  toggleSpinnerSelector,
  interactionSelector,
  portfolioDataSelector
} from "../../redux/selectors";
import {
  useAppSelector,
  useAppDispatch
} from "../../redux/store";
import Button from "../../components/Button";
import grantIcon from "../../assets/grant.svg";
import inkind from "../../assets/inkind.svg";
import volunteer from "../../assets/volunteer.svg";
import LinkComponent from "../../components/LinkComponent";
import SkeletonView from "./Skeleton"

const formItemLayout = {
  labelCol: { span: 2 },
  wrapperCol: { span: 20 },
};

function formatToYYYYMMDD(dateString) {
  const date = new Date(dateString);
  return date.getUTCFullYear() +
    String(date.getUTCMonth() + 1).padStart(2, '0') +
    String(date.getUTCDate()).padStart(2, '0');
}

const ProgramReview = ({ className }) => {
  const baseClassName = clsx("programReview", className);
  const intl = useIntl();
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [selectedProject, setSelectedProject] = useState(null);
  const [formData, setFormData] = useState({});
  const [documentUrls, setDocumentUrls] = useState({});
  const [mediaImages, setMediaImages] = useState([]);
  const showSpinner = useAppSelector(toggleSpinnerSelector);
  const contentListData = useAppSelector(portfolioDataSelector) || '';
  const interactionsData = useAppSelector(interactionSelector);
  const { reportType, programID, startDate, closedDate } = formData || {};

  const separateBySpecificTypes = (data) => {
    if (!data?.length) return
    return data?.reduce((acc, item) => {
      if (['Grant', 'Volunteering', 'In-Kind']?.includes(item.type)) {
        acc[item.type]?.push(item);
      }
      return acc;
    }, { Grant: [], Volunteering: [], 'In-Kind': [] });
  }

  const { Grant, Volunteering, 'In-Kind': InKind } = separateBySpecificTypes(selectedProject?.donations) || {};


  const { opportunitySummary,
    gallery,
    caseStudy,
  } = selectedProject || {};

  useEffect(() => {
    if (contentListData?.length > 0) {
      const firstProgramName = contentListData?.[0];
      setSelectedProject(firstProgramName);
      dispatch(toggleSpinner(false));
    }
  }, [contentListData])

  useEffect(() => {
    dispatch(getInteraction({
      sortBy: "programName",
      asc: "true",
      filterBy: {
        status: [
          "Approved",
          "Completed"
        ]
      },
      limit: "100"
    })
    )
  }, []);

  useEffect(() => {
    const fetchDocumentUrls = async () => {
      try {
        if (caseStudy?.length > 0) {
          const promises = caseStudy.map(async (el) => {
            if (el?.documents?.length > 0 && (el?.quoteBy || el?.quoteby) && !documentUrls[el.quoteBy]?.length) {
              const documentPromises = el.documents.map(async (doc) => {
                const securedURI = await getSecuredUrl(doc.key);
                const securedURL = URL.createObjectURL(securedURI);
                return { url: securedURL, name: doc.name };
              });

              const resolvedDocuments = await Promise.all(documentPromises);
              return { quoteBy: el.quoteBy, documents: resolvedDocuments.filter(Boolean) };
            }
            return null;
          });

          const resolvedDocumentData = await Promise.all(promises);
          const validDocuments = resolvedDocumentData.filter(Boolean);

          setDocumentUrls((prev) => ({
            ...prev,
            ...validDocuments.reduce((acc, { quoteBy, documents }) => {
              acc[quoteBy] = documents;
              return acc;
            }, {}),
          }));
        }
      } catch (error) {
        console.error('Error fetching document URLs:', error);
      }
    };

    fetchDocumentUrls();
  }, [caseStudy]);

  useEffect(() => {
    const fetchMediaImages = async () => {
      try {
        if (gallery?.length > 0 && !mediaImages?.length) {
          const promises = gallery.map(async (file) => {
            const key = file?.key || file?.fileKey;
            const securedURI = await getSecuredUrl(key);
            const securedURL = URL.createObjectURL(securedURI);
            return { url: securedURL, name: file.name, mediaDescription: file.mediaDescription };
          });
          const resolvedMediaImages = await Promise.all(promises);
          const validMediaImages = resolvedMediaImages.filter(Boolean);
          setMediaImages(validMediaImages);
        }
      } catch (error) {
        console.error('Error fetching media images:', error);
      }
    };

    fetchMediaImages();
  }, [gallery]);

  const onFinishFailed = (e) => {
    console.log("Failed:", e);
  };

  const onFormChange = (_, allValues) => {
    setFormData(allValues);
  };

  const onFinish = (e) => {
    const { startDate, closedDate } = form.getFieldsValue();

    // Manual validation for dates
    if (startDate && !closedDate) {
      form.setFields([
        {
          name: 'closedDate',
          errors: ['Please select an end date if a start date is chosen.'],
        },
      ]);
      return;
    }

    if (closedDate && !startDate) {
      form.setFields([
        {
          name: 'startDate',
          errors: ['Please select a start date if an end date is chosen.'],
        },
      ]);
      return;
    }

    if (startDate && closedDate && !closedDate.isAfter(startDate)) {
      form.setFields([
        {
          name: 'closedDate',
          errors: ['End date must be after the start date.'],
        },
      ]);
      return;
    }

    // If validation passes, proceed with the form submission
    if (programID || reportType || (startDate && closedDate)) {
      dispatch(toggleSpinner(true));
      dispatch(getOperationDashboard({
        programID,
        openDate: startDate ? formatToYYYYMMDD(startDate) : undefined,
        closeDate: closedDate ? formatToYYYYMMDD(closedDate) : undefined,
        action: "content",
      }));
    }
  };

  return (
    <div className={baseClassName}>
      <div className="leftSubContainer">
        <div className='subContentContainer'>
          <StyledText as="p" variant="H2" className='title'>
            {intl.formatMessage({ id: "consolidated_report" })}
          </StyledText>
          <Form
            {...formItemLayout}
            labelAlign="left"
            form={form}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            className="formContainer"
            scrollToFirstError
            onValuesChange={onFormChange}
          >
            <div className="dropDownSubContainer">
              <StyledText as="p" variant="B3d" className='dropdownLabel'>
                {intl.formatMessage({ id: "portfolio" })}
              </StyledText>
              <Form.Item name="portfolio">
                <Select
                  disabled
                  allowClear
                  className="selectBox"
                  placeholder={intl.formatMessage({ id: "portfolio_name" })}
                  type="formSelect"
                  options={[]}
                />
              </Form.Item>
            </div>
            <div className="dropDownSubContainer">
              <StyledText as="p" variant="B3d" className='dropdownLabel'>
                {intl.formatMessage({ id: "select_programme" })}
              </StyledText>
              <Form.Item name="programID">
                <Select
                  allowClear
                  className="selectBox"
                  placeholder={intl.formatMessage({ id: "select_programme" })}
                  type="formSelect"
                  mode="multiple"
                  options={interactionsData
                    ?.map((el) => ({
                      key: el?.programID,
                      label: el?.programName,
                      value: el?.programID,
                    }))
                    .filter((program, index, self) =>
                      index === self.findIndex((p) => p.label === program.label)
                    )}
                />
              </Form.Item>
            </div>
            <div className="dropDownSubContainer">
              <StyledText as="p" variant="B3d" className='dropdownLabel'>
                {intl.formatMessage({ id: "period" })}
              </StyledText>
              <div className="dateContainer">
                <Form.Item
                  name="startDate"
                  className="formItem smallInput selectBox"
                >
                  <DatePicker
                    format="YYYY-MM-DD"
                    placeholder="Enter start date"

                  />
                </Form.Item>
                <div className="dropDownSubContainer">
                  <Form.Item
                    name="closedDate"
                    className="formItem smallInput selectBox"
                  >
                    <DatePicker
                      format="YYYY-MM-DD"
                      placeholder="Enter end date"

                    />
                  </Form.Item>
                </div>
                <Button variant="primary" htmlType="submit">
                  <StyledText as="p" variant="B3d" className="generate">
                    {intl.formatMessage({ id: "generate" })}
                  </StyledText>
                </Button>
              </div>
            </div>
          </Form>
        </div>
        {contentListData?.length > 0 && <StyledText as="p" variant="H1">
          {intl.formatMessage({ id: "overview" })}
        </StyledText>}
        {showSpinner ? <SkeletonView /> : contentListData?.length > 0 && <div className="parentContainer">
          <div className="leftSubContainer">
            <StyledText as="p" variant="H1" className="allProjects divider">
              {intl.formatMessage({ id: "all_projects" })}
            </StyledText>
            <Divider />
            <div className="opportunityName">
              <StyledText as="p" variant="H1">
                {selectedProject?.opportunityName}
              </StyledText>
              <LinkComponent href={`${window.location.origin}/company/content/${selectedProject?.contentID}?type=final`} target="_blank" rel="noreferrer">
                <StyledText as="p" variant="B3">
                  {intl.formatMessage({ id: "see_full_report" })}
                </StyledText>
              </LinkComponent>
            </div>
            <div className="subContainer">
              <StyledText as="p" variant="H2">
                {intl.formatMessage({ id: "summary_title" })}
              </StyledText>
              <StyledText as="p" variant="B3">
                {opportunitySummary}
              </StyledText>
            </div>
            <div className="subContainer">
              <StyledText as="p" variant="H2">
                {intl.formatMessage({ id: "support_awarded" })}
              </StyledText>
              <div className="supportFlexContainer">
                {Grant?.length > 0 && <div className="supportContainer grantContainer">
                  <div className="titleContainer">
                    <div>
                      <Avatar src={grantIcon} />
                    </div>
                    <div>
                      <StyledText as="p" variant="H3">
                        {intl.formatMessage({ id: "grants" })}
                      </StyledText>
                    </div>
                  </div>
                  <div className="flexBox">
                    <div className="flexBoxChild">
                      <div className="flexColumn">
                        {Grant?.map((el) => <StyledText key={el?.donationAmount?.trim()} as="p" variant="B3c">
                          {el?.currencySymbol || '$'}
                          {el?.donationAmount?.trim()}
                        </StyledText>)}
                      </div>
                    </div>
                  </div>
                </div>
                }
                {Volunteering?.length > 0 && <div className="supportContainer volunteeringContainer">
                  <div className="titleContainer">
                    <div>
                      <Avatar src={volunteer} />
                    </div>
                    <div>
                      <StyledText as="p" variant="H3">
                        {intl.formatMessage({ id: "volunteering" })}
                      </StyledText>
                    </div>
                  </div>
                  <ul>
                    {Volunteering.map((el) => (
                      <li key={el?.donationAmount}>
                        <StyledText as="span" variant="B3c">
                          {`${el?.donationAmount} ${intl.formatMessage({ id: "days" })}`}
                        </StyledText>
                      </li>
                    ))}
                  </ul>
                </div>}
                {InKind?.length > 0 && <div className="supportContainer inKindContainer">
                  <div className="titleContainer">
                    <div>
                      <Avatar src={inkind} />
                    </div>
                    <div>
                      <StyledText as="p" variant="H3">
                        {intl.formatMessage({ id: "inKind" })}
                      </StyledText>
                    </div>
                  </div>
                  <ul>
                    {InKind?.map((el) => (
                      <li key={el?.inKindActivityDescription}>
                        <StyledText as="span" variant="B3">
                          {`${el?.inkindDescription} ${intl.formatMessage({ id: "of_equivalent_value" })}`}
                          <StyledText as="span" variant="B3c">
                            {` ${el.currencySymbol}${el?.donationAmount}`}
                          </StyledText>
                        </StyledText>
                      </li>
                    ))}
                  </ul>
                </div>}
              </div>
            </div>
            <div className="subContainer">
              {caseStudy?.map((el) => (
                <div className="subWhiteContainer" key={(el?.quoteBy || el?.quoteby) || el?.name}>
                  <StyledText
                    as="p"
                    variant="B3c"
                    style={{
                      background: '#CBEAFF',
                      borderRadius: '4px',
                      width: 'max-content',
                      paddingInline: '4px',
                    }}
                  >
                    {`${intl.formatMessage({ id: 'case_study' })}`}
                  </StyledText>
                  <div className="contentContainer">
                    <div className="leftContent">
                      <StyledText as="p" variant="B3" style={{ fontSize: '16px', color: '#759CC7' }}>
                        {el.name}
                      </StyledText>
                      <StyledText as="p" variant="B3d">
                        {intl.formatMessage({ id: 'what_impact_did_your_project_have' })}
                      </StyledText>
                      <StyledText as="p" variant="B3">
                        {el?.outcome}
                      </StyledText>
                      <div className="quoteContainer">
                        <StyledText as="p" variant="B3" style={{ fontStyle: 'italic' }}>
                          {el?.shareQuote}
                        </StyledText>
                        <StyledText as="p" variant="B3" style={{ fontStyle: 'italic' }}>
                          {(el?.quoteBy || el?.quoteby)}
                        </StyledText>
                      </div>
                    </div>
                    <div className="mediaContainer">
                      {(el?.quoteBy || el?.quoteby) && documentUrls[el.quoteBy]?.length === el?.documents?.length && (
                        <MediaCarousel data={documentUrls[el.quoteBy]} />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {Array.isArray(gallery) && gallery?.length > 0 && <div className="subContainer mediaSection">
              <StyledText as="p" variant="H2">
                {intl.formatMessage({ id: "media" })}
              </StyledText>
              {mediaImages?.length === gallery?.length && <MediaCarousel data={mediaImages} />}
            </div>}
          </div>
          <div className="rightSubContainer">
            <StyledText as="p" variant="H1" className="allProjects">
              {intl.formatMessage({ id: "all_projects" })}
            </StyledText>
            <Divider />
            {contentListData?.length > 0 && contentListData?.map((el, index) => <div key={`${el?.opportunityName}-${el?.programName}-${index}`} className="mainContent">
              <button className="programNameButton" onClick={() => setSelectedProject(el)}>
                <StyledText as="p" variant="H3" className='subTitle'>
                  {el?.opportunityName}
                </StyledText>
              </button>
            </div>)}
          </div>

        </div>}
      </div>
    </div>
  );
};

const StyledProgramReview = styled(ProgramReview)`
  &.programReview {
    display: flex;
    gap: 24px;
    padding-bottom: 60px;
    padding: 20px;
    background: #F2F4F8;
    .generate{
      color: white;
    }
    .opportunityName{
      background: rgba(4, 105, 172, 0.1);
      border-radius: 10px;
      padding: 5px 10px;
      display: flex;
      justify-content: space-between;
      &.skeleton{
            padding: 20px;
          }
    }
    .mainContent{
      overflow-y: auto;
      &.skeleton{
            padding: 20px;
          }
    }
.parentContainer{
  display: flex;
  gap: 24px;
  &.skeleton{
            padding: 20px;
          }
}
.allProjects{
  padding-top: 10px;
  &.divider{
    margin-bottom: -20px;
  }
}
    .rightSubContainer{
      height: 60vh;
      overflow-y: scroll;
      gap: 10px;
      display: flex;
      flex-direction: column;
      background: white;
        box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          padding: 0 16px;
          width: 20%;
          &.skeleton{
            padding: 20px;
          }
      .programNameButton{
        width: 100%;
        display: flex;
        text-align: left;
        background: transparent;
        border: none;
        cursor: pointer;
        text-decoration: underline;
        &.skeleton{
            padding: 20px;
          }
         &:hover{
          color: #04ac9c;
          background: #F2FDF9;
          border-radius: 10px;
          p{
            color: #04ac9c;
            background: #F2FDF9;
          }
        }
      }
    }

    .subContentContainer{
      background: transparent linear-gradient(136deg, #000F19 0%, #0469AC 100%) 0% 0% no-repeat padding-box;
      box-shadow: 0px 3px 16px #0000000D;
      border-radius: 10px;
      padding: 20px;
      &.skeleton{
            padding: 20px;
          }
    }

    .dropdownLabel{
      color: #fff;
      &.skeleton{
            padding: 20px;
          }
    }

    .dropDownSubContainer{
      display: flex;
      flex-direction: column;
      gap: 10px;
      .ant-form-item{
        width: 250px !important;
      }
      .ant-picker{
        width: 200px !important;
      }
    }

    .title{
      margin-bottom: 15px;
      font-size: 20px;
      font-family: ${constants.POPPINS_FONT_FAMILY};
      font-weight: 600;
      color: #FFFFFF;
      &.skeleton{
            padding: 20px;
          }
    }

    .contentContainer{
      display: flex;
      justify-content: space-between;
      &.skeleton{
            padding: 20px;
          }
      .leftContent{
        display: flex;
        flex-direction: column;
        gap: 10px;
        &.skeleton{
            padding: 20px;
          }
      }
      .mediaContainer{
        width: 20vw;
        .carouselImage {
      width: 100%;
      height: 150px;
      object-fit: contain;
    }
        .ant-carousel, .slick-initialized  {
          height: 150px;
        }
        .ant-carousel .slick-dots-bottom {
          bottom: 0 !important;
        }
      }
    }

   .formContainer{
      display: flex;
      gap: 50px;
      &.skeleton{
            padding: 20px;
          }
    }

    .dateContainer{
      display: flex;
      gap: 50px;
    }
    .supportParentContainer{
      display: flex;
      gap: 10px;
    }
    .switchItemContainer{
      width: 100%;
      display: flex;
      justify-content: flex-end;

      .switchItem{
      width: max-content;
      }
    }

    .quoteContainer{
      border-left: 2px solid black;
      padding: 5px 10px;
    }

    .contentContainer{
      display: flex;
      justify-content: space-between;
      &.skeleton{
            padding: 20px;
          }
      .leftContent{
        display: flex;
        flex-direction: column;
        gap: 10px;
        &.skeleton{
            padding: 20px;
          }
      }
      .mediaContainer{
        width: 20vw;
        .carouselImage {
      width: 100%;
      height: 150px;
      object-fit: contain;
    }
        .ant-carousel, .slick-initialized  {
          height: 150px;
        }
        .ant-carousel .slick-dots-bottom {
          bottom: 0 !important;
        }
      }
    }

    .overlay {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 1000;
        height: 100%;
        background-color: rgba(255, 255, 255, 0.45);
    }
        .spinner {
        z-index: 2000;
        margin: auto;
        left: 50%;
        right: 50%;
        position: absolute;
        bottom: 50%;
        }
            ul {
            margin: 0;
            }

    .leftSubContainer{
        width: 95%;
        display: flex;
        flex-direction: column;
        gap: 30px;
        &.skeleton{
            padding: 20px;
          }
    } 

    .whiteSupportContainer{
        background: white;
        box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 16px;
          width: 35vw;
          &.volunteeringContainer {
         box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
    }

    .ant-collapse-expand-icon {
      color: #313131 !important;
    }

    .mediaSection{
        margin-bottom: 20px;
    }

    .flexBox{
        display: flex;
        align-items: flex-start;
        gap: 20px;
        margin-left: 35px;
        &.justifyContent{
            justify-content: flex-start;
        }
        .flexBoxChild{
            display: flex;
            flex-direction: column;
            gap: 10px;
            .flexColumn{
                display: flex;
                flex-direction: column;
                .noBreak{
                    white-space: nowrap;
                }
            }
        }
    }

    .divider{
        height: 100%;
    margin-inline: 10px;
    }
    .outputNumber{
        background: #dee3ed;
        border-radius: 5px;
        padding: 1px 8px;
        border: 1px solid grey; 
    }

    .supportFlexContainer {
      display: flex;
      gap: 30px;
      flex-direction: column;

      .supportContainer {
        padding: 16px;
        width: max-content;
        width: 35vw;

        &.grantContainer {
          background: transparent linear-gradient(223deg, #ffffff 0%, #b2f9bd 100%) 0% 0% no-repeat padding-box;
          box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 0px;
          &.skeleton{
            padding: 20px;
          }
        }

        &.volunteeringContainer {
          background: transparent linear-gradient(223deg, #ffffff 0%, #8bdaff 100%) 0% 0% no-repeat padding-box;
          box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 0px;
          &.skeleton{
            padding: 20px;
          }
        }

        &.inKindContainer {
          background: transparent linear-gradient(223deg, #ffffff 0%, #ffaffa 100%) 0% 0% no-repeat padding-box;
          box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          &.skeleton{
            padding: 20px;
          }
        }

        
      }
    }
    .titleContainer {
          display: flex;
          gap: 2px;
          align-items: center;
          &.skeleton{
            padding: 20px;
          }
        }
    }
    .helperText {
    color: #5271FF !important;
  }

    .subContainer {
      display: flex;
      flex-direction: column;
      gap: 10px;
      &.skeleton{
            padding: 20px;
          }
      .descriptionContainer{
        max-width: 70ch;
            word-wrap: break-word;
            overflow-wrap: break-word;
            &.skeleton{
            padding: 20px;
          }
        p, li{
            font-size: 14px;
            font-weight: 500;
            color: #242424;
            font-family: sans-serif;
            max-width: 70ch;
            word-wrap: break-word;
            overflow-wrap: break-word;
        }
      }
    }

    .subWhiteContainer{
      display: flex;
      flex-direction: column;
      gap: 10px;
      background: white;
      border-radius: 10px;
      padding: 10px;
      &.skeleton{
            padding: 20px;
          }
    }

    .ant-collapse-expand-icon {
      transform: rotate(90deg) translate(-5px);
      svg{
            fill: #313131 !important;
        }
    }

    .ant-collapse-item-active {
      .ant-collapse-expand-icon {
        transform: rotate(0deg) translate(-5px);
      }
    }

    .ant-timeline-item-head {
      background: transparent !important;
    }

    .anticon-check {
      color: #58b55e;
    }
    .supportContainer {
    padding: 16px;
    width: max-content;
    width: 35vw;
    .divider{
        height: 100%;
    margin-inline: 10px;
    }
    &.grantContainer {
      background: transparent linear-gradient(223deg, #ffffff 0%, #b2f9bd 100%) 0% 0% no-repeat padding-box;
      box-shadow: 0px 3px 16px #0000000d;
      border-radius: 10px;
      display: flex;
      flex-direction: column;
      gap: 0px;
      &.skeleton{
            padding: 20px;
          }
    }
  }
  .additionalFunding{
        background: white !important;
        padding: 20px;
        border-radius: 10px;
        display: flex;
        flex-direction: column;
        gap: 20px;
    }
  .expenditureValues{
        background: #5DB76A;
        border-radius: 5px;
        padding: 1px 8px;
        border: 1px solid #5DB76A;
    }
    .supportContainer {
    padding: 16px;
    width: max-content;
    width: 35vw;
    .divider{
        height: 100%;
    margin-inline: 10px;
    }
    &.volunteeringContainer {
          background: transparent linear-gradient(223deg, #ffffff 0%, #8bdaff 100%) 0% 0% no-repeat padding-box;
          box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 0px;
        }
  }
  .titleContainer {
          display: flex;
          gap: 2px;
          align-items: center;
        }
    .supportContainer {
    padding: 16px;
    width: max-content;
    width: 35vw;
        &.inKindContainer {
          background: transparent linear-gradient(223deg, #ffffff 0%, #ffaffa 100%) 0% 0% no-repeat padding-box;
          box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
        }
  }
  .outputNumber{
        background: #dee3ed;
        border-radius: 5px;
        padding: 1px 8px;
        border: 1px solid grey; 
    }
    .unsdgPartitionContainer {
      display: flex;
      gap: 20px;
      background: white;
    padding: 30px;
    border-radius: 10px;

      .collapseContainer {
        width: 50%;
        button {
            width: 100%;
        }
      }
      .unsdgsContainerContainer{
        display: flex;
        flex-direction: column;
        gap: 15px;
          width: 48%;
      }
      .unsdgsContainer {
        display: flex;
        gap: 15px;
        flex-wrap: wrap;
        height: max-content;

        .showAllGoals {
          height: 100px;
          width: 100px;
          background: white;
          display: flex;
          flex-direction: column;
          padding: 15px;
          justify-content: center;
          align-items: center;
          text-align: center;
          border: 2px solid #00877a;
          border-radius: 6px;

          p,
          svg {
            color: #00877a !important;
          }
        }

        .unsdgs {
          height: 100px;
          width: 100px;
        }
      }
    }

    .expandAll {
      color: #00877a;
      text-align: end;
      background: transparent;
      border: 0;
      cursor: pointer;
    }

    .ant-collapse{
        border: 0;
        border-bottom: 1px solid #D7DDE1;
        .ant-collapse-header{
        background: white !important;
        }
    }
    .ant-collapse-content-active{
        border-top: 0;
    }
     .collapseChildContainer {
          border: 1px solid #d7dde1;
          padding: 12px 16px;
          margin-bottom: 6px;
          cursor: pointer;
          text-align: left;
          background: white;
          font-size: 14px;
          font-weight: ${constants.FONT_WEIGHT_SEMIBOLD};
          color: ${constants.COLOR_GREY_DARK};
          font-family: ${constants.POPPINS_FONT_FAMILY};
    
          &.selectedName {
            border: 1px solid #04ac9c;
            border-radius: 6px;
            background: #bcfbf5;
          }
        }
    
        .collapsible {
          display: flex;
          flex-direction: column;
    
          .collapseChildContainer {
            background: white;
            border: 0;
            border-bottom: 1px solid #d9d9d9;
            padding: 12px 16px;
            text-align: left;
    
            &.selectedName {
              border: 1px solid #04ac9c;
              background: #bcfbf5;
            }
    
            cursor: pointer;
          }
        }
        .ant-collapse-expand-icon {
      transform: rotate(90deg) translate(-5px);
      svg{
            fill: #313131 !important;
        }
    }

    .ant-collapse-item-active {
      .ant-collapse-expand-icon {
        transform: rotate(0deg) translate(-5px);
      }
    }
  }
`;

export default StyledProgramReview;